'use client';

import { useRef } from 'react';
import { routePath } from '@/lib/routes';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER, MonoCaption } from './bits';
import DossierHead from './DossierHead';
import { useChoreo } from './motion';

/**
 * §04 — the honest link card (surface-2 band). Two identical OG unfurls —
 * same image block, title, description — only the domain row differs,
 * rendered as full-bleed diff stripes because that IS the diff. Concedes in
 * copy that Bitly does branded domains too (honesty rail: this is the
 * argument for custom domains anywhere, not a scoreboard row).
 */

function Unfurl({
  label,
  labelClass,
  cardClass,
  stripe,
}: {
  label: string;
  labelClass: string;
  cardClass: string;
  stripe: React.ReactNode;
}) {
  return (
    <div data-unfurl className="flex min-w-[280px] max-w-[480px] flex-[1_1_340px] flex-col gap-2.5">
      <span className={`font-jetbrains text-[10px] tracking-[0.14em] ${labelClass}`}>{label}</span>
      <div
        aria-hidden="true"
        className={`overflow-hidden rounded-ref bg-surface shadow-ref ${cardClass}`}
      >
        <div className="flex aspect-[1.91/1] flex-col justify-center gap-1.5 bg-ink px-7">
          <span className="font-onest text-[26px] font-extrabold tracking-[-0.02em] text-white">
            ACME
          </span>
          <span className="font-jetbrains text-[10.5px] tracking-[0.14em] text-white/55">
            AUTUMN LINE &mdash; LIVE NOW
          </span>
        </div>
        <div className="flex flex-col gap-1 px-[18px] py-3.5">
          <span className="text-[14.5px] font-semibold text-ink">
            Acme &mdash; the autumn line is live
          </span>
          <span className="text-[12.5px] leading-[1.5] text-muted">
            Every fit, four new colorways. Free returns through October.
          </span>
          {stripe}
        </div>
      </div>
    </div>
  );
}

export default function HonestLinkCard({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelectorAll('[data-unfurl]'), {
      y: 26,
      opacity: 0,
      duration: 0.75,
      stagger: 0.15,
    });
    tl.from(root.querySelector('[data-fair-note]'), { y: 14, opacity: 0, duration: 0.6 }, '-=0.35');
    tl.from(
      root.querySelector('[data-diff-wipe]'),
      { scaleX: 0, transformOrigin: 'left center', duration: 0.6, ease: 'power2.inOut' },
      '-=0.2',
    );
    tl.from(
      root.querySelector('[data-diff-plus]'),
      { scale: 0, duration: 0.3, ease: 'back.out(2.5)' },
      '-=0.15',
    );
    tl.from(root.querySelector('[data-diff-caption]'), { opacity: 0, duration: 0.45 }, '-=0.1');
    return tl;
  });

  return (
    <section ref={rootRef} className="mt-16 border-y border-line bg-surface-2 md:mt-24">
      <div className={`${CONTAINER} py-14 md:py-[88px]`}>
        <DossierHead
          num="04"
          label="THE HONEST LINK CARD"
          title="Same page. Same preview. One different pixel."
          lead="Two unfurls of the same URL, as a chat app renders them. The title, image and description come from the page&rsquo;s own OG tags — identical twice. The only thing that changes is the domain, and it&rsquo;s the line people check before they tap."
          leadClassName="max-w-[600px]"
        />
        <p className="sr-only">
          Two link previews with identical images, titles and descriptions; the first shows the
          default short domain rii.link/x8f2, the second shows a branded domain go.acme.com/launch
          — the domain is the only visible difference.
        </p>
        <div className="mt-8 flex flex-wrap items-stretch gap-6 md:mt-11">
          <Unfurl
              label="DEFAULT SHORT DOMAIN"
              labelClass="text-dim"
              cardClass="border border-line-2"
              stripe={
                <span className="-mx-[18px] -mb-3.5 mt-2 block border-t border-line bg-bg-warm px-[18px] pb-[13px] pt-2 font-jetbrains text-[12px] text-muted">
                  <span className="text-dim">&minus;&nbsp;&nbsp;</span>rii.link/x8f2
                </span>
              }
            />
          <Unfurl
              label="YOUR DOMAIN"
              labelClass="text-brand-600"
              cardClass="border border-[color-mix(in_srgb,var(--color-brand-600)_40%,var(--color-line-2))]"
              stripe={
                <span className="relative -mx-[18px] -mb-3.5 mt-2 block overflow-hidden border-t border-[color-mix(in_srgb,var(--color-data-green)_35%,transparent)] px-[18px] pb-[13px] pt-2 font-jetbrains text-[12px] font-semibold text-ink">
                  <span
                    data-diff-wipe
                    aria-hidden="true"
                    className="absolute inset-0 bg-[color-mix(in_srgb,var(--color-data-green)_9%,white)]"
                  />
                  <span className="relative">
                    <span data-diff-plus className="inline-block text-data-green">
                      +
                    </span>
                    &nbsp;&nbsp;go.acme.com/launch
                  </span>
                </span>
              }
            />
          <div
            data-fair-note
            className="flex min-w-[240px] flex-[1_1_260px] flex-col justify-center gap-3.5"
          >
            <p className="text-[14.5px] leading-[1.65] text-pretty text-muted">
              {data.fairNote}
            </p>
            <a
              href={routePath('customDomains')}
              className="text-sm font-semibold text-brand-600 hover:text-brand-700"
            >
              Bring your domain &rarr; /custom-domains
            </a>
          </div>
        </div>
        <MonoCaption data-diff-caption className="mt-5">
          SAME OG TAGS, RENDERED TWICE &mdash; THE CHANGED LINE IS MARKED LIKE A DIFF, BECAUSE IT
          IS ONE
        </MonoCaption>
      </div>
    </section>
  );
}
